import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, Fade, makeStyles, alpha } from '@material-ui/core';
import TextBoxWithTooltip from '../../textBoxWithTooltip';
import Tooltip from '../../tooltip';
import { ReactComponent as TextReplyIcon } from '../../../../assets/icons/text_snippet.svg';
import { ReactComponent as SelfReviewIcon } from '../../../../assets/icons/self-assesment-small_purple.svg';
import { ReactComponent as TopDownReviewIcon } from '../../../../assets/icons/top_down_review_small.svg';
import { ReactComponent as DownUpReviewIcon } from '../../../../assets/icons/down_up_review_small.svg';
import { CYCLE_STEP_TYPES } from '../../../../constants/cycle';
import { getProgress } from '../../../../utility/uiUtils';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  nameWrapper: {
    display: 'grid',
    gap: 8,
    gridTemplateColumns: '1fr max-content',
    alignItems: 'center',
    marginBottom: spacing(3),
  },
  attribute: {
    boxSizing: 'border-box',
    backgroundColor: ({ color, isTextReply }) =>
      isTextReply ? primary.bluish8 : color,
    border: ({ color, isTextReply }) =>
      isTextReply ? `4px solid ${color}` : 'none',
    height: 28,
    position: 'relative',
    zIndex: 9,
  },
  rangeWrapper: {
    display: 'grid',
    gap: 3,
    alignItems: 'center',
    gridTemplateColumns: 'max-content max-content',
  },
  range: {
    boxSizing: 'border-box',
    backgroundColor: alpha(primary.black, 0.2),
    position: 'absolute',
    top: 0,
    height: 28,
    zIndex: 8,
  },
  rating: {
    backgroundColor: primary.white,
    position: 'absolute',
    width: 14,
    height: 14,
    transform: 'translate(-50%)',
  },
  ratingIcon: {
    width: 14,
    height: 14,
    '& path': {
      fill: primary.bluish1,
    },
  },
  selfRating: {
    bottom: '-14px',
  },
  topDownRating: {
    bottom: '-32px',
  },
  downUpRating: {
    bottom: '-50px',
  },
  line: {
    borderLeft: `2px dotted ${primary.bluish1}`,
    position: 'absolute',
    top: 0,
    transform: 'translate(-50%)',
    height: 28,
  },
  openEndedIcon: {
    cursor: 'default',
    width: 20,
    height: 20,
  },
}));

const AttributeRatings = ({ translations, attribute, visibleRatings }) => {
  const { SELF_REVIEW_STEP, TOP_DOWN_STEP, DOWN_UP_STEP } = CYCLE_STEP_TYPES;
  const { name, color, text_reply_questions, expected_range, scores } =
    attribute;
  const [from, to] = expected_range || [];
  const classes = useStyles({
    color,
    isTextReply: text_reply_questions,
  });

  const hasRange = expected_range && !text_reply_questions;
  const isSelfVisible =
    scores[SELF_REVIEW_STEP] && visibleRatings[SELF_REVIEW_STEP];
  const isTopDownVisible =
    scores[TOP_DOWN_STEP] && visibleRatings[TOP_DOWN_STEP];
  const isDownUpVisible = scores[DOWN_UP_STEP] && visibleRatings[DOWN_UP_STEP];
  const selfScore = scores[SELF_REVIEW_STEP];
  const topDownScore = scores[TOP_DOWN_STEP];
  const downUpScore = scores[DOWN_UP_STEP];

  return (
    <div>
      <div className={classes.nameWrapper}>
        <TextBoxWithTooltip variant="h5" text={name} />
        {hasRange && (
          <div className={classes.rangeWrapper}>
            <Typography variant="body2">{translations.levelRange}</Typography>
            <Typography variant="subtitle2">{`${from} - ${to}`}</Typography>
          </div>
        )}
        {text_reply_questions && (
          <Tooltip
            customIconClass={classes.openEndedIcon}
            text={translations.openEndedAttribute}
            icon={TextReplyIcon}
          />
        )}
      </div>
      <div className={classes.attribute}>
        {hasRange && (
          <div
            className={classes.range}
            style={{
              width: `${(to - from) * 10}%`,
              left: getProgress(from),
            }}
          />
        )}
        <Fade in={isSelfVisible}>
          <div
            className={classNames(classes.rating, classes.selfRating)}
            style={{
              left: selfScore ? getProgress(selfScore) : 0,
            }}
          >
            <Tooltip
              customIconClass={classes.ratingIcon}
              text={`${translations.self}: ${selfScore}`}
              icon={SelfReviewIcon}
            />
          </div>
        </Fade>
        <Fade in={isSelfVisible}>
          <div
            className={classes.line}
            style={{
              left: selfScore ? getProgress(selfScore) : 0,
            }}
          />
        </Fade>
        <Fade in={isTopDownVisible}>
          <div
            className={classNames(classes.rating, classes.topDownRating)}
            style={{
              left: topDownScore ? getProgress(topDownScore) : 0,
            }}
          >
            <Tooltip
              customIconClass={classes.ratingIcon}
              text={`${translations.topDown}: ${topDownScore}`}
              icon={TopDownReviewIcon}
            />
          </div>
        </Fade>
        <Fade in={isTopDownVisible}>
          <div
            className={classes.line}
            style={{
              left: topDownScore ? getProgress(topDownScore) : 0,
            }}
          />
        </Fade>
        <Fade in={isDownUpVisible}>
          <div
            className={classNames(classes.rating, classes.downUpRating)}
            style={{
              left: downUpScore ? getProgress(downUpScore) : 0,
            }}
          >
            <Tooltip
              customIconClass={classes.ratingIcon}
              text={`${translations.downUp}: ${downUpScore}`}
              icon={DownUpReviewIcon}
            />
          </div>
        </Fade>
        <Fade in={isDownUpVisible}>
          <div
            className={classes.line}
            style={{
              left: downUpScore ? getProgress(downUpScore) : 0,
            }}
          />
        </Fade>
      </div>
    </div>
  );
};

AttributeRatings.propTypes = {
  translations: PropTypes.object.isRequired,
  attribute: PropTypes.object.isRequired,
  visibleRatings: PropTypes.object.isRequired,
};

export default AttributeRatings;
